/**
 * Indian & Hindu Calendar Holiday Database & Suggestion Engine
 * Covers Gazetted National, Hindu Festivals, and Cultural Holidays
 */

export interface HolidaySuggestion {
  date: string; // YYYY-MM-DD
  name: string;
  category: 'Hindu Festival' | 'National Holiday' | 'Gazetted' | 'Weekend';
  isCommon?: boolean;
}

// Fixed National / Recurring Solar Holidays (applied to any year)
const FIXED_ANNUAL_HOLIDAYS: Array<{ monthDay: string; name: string; category: HolidaySuggestion['category'] }> = [
  { monthDay: '01-01', name: "New Year's Day", category: 'National Holiday' },
  { monthDay: '01-14', name: 'Makar Sankranti / Uttarayan / Pongal', category: 'Hindu Festival' },
  { monthDay: '01-26', name: 'Republic Day', category: 'National Holiday' },
  { monthDay: '04-14', name: 'Dr. B.R. Ambedkar Jayanti', category: 'National Holiday' },
  { monthDay: '05-01', name: 'Gujarat Day / Labour Day', category: 'National Holiday' },
  { monthDay: '08-15', name: 'Independence Day', category: 'National Holiday' },
  { monthDay: '10-02', name: 'Mahatma Gandhi Jayanti', category: 'National Holiday' },
  { monthDay: '12-25', name: 'Christmas Day', category: 'Gazetted' },
];

// Year-specific lunar/astronomical festival dates
const FESTIVALS_BY_YEAR: Record<number, Array<{ monthDay: string; name: string; category: HolidaySuggestion['category'] }>> = {
  2025: [
    { monthDay: '02-02', name: 'Vasant Panchami', category: 'Hindu Festival' },
    { monthDay: '02-26', name: 'Maha Shivratri', category: 'Hindu Festival' },
    { monthDay: '03-13', name: 'Holika Dahan', category: 'Hindu Festival' },
    { monthDay: '03-14', name: 'Holi / Dhuleti', category: 'Hindu Festival' },
    { monthDay: '03-30', name: 'Chaitra Navratri / Gudi Padwa / Ugadi', category: 'Hindu Festival' },
    { monthDay: '03-31', name: 'Eid-ul-Fitr (Ramzan Id)', category: 'Gazetted' },
    { monthDay: '04-06', name: 'Ram Navami', category: 'Hindu Festival' },
    { monthDay: '04-10', name: 'Mahavir Jayanti', category: 'Gazetted' },
    { monthDay: '04-18', name: 'Good Friday', category: 'Gazetted' },
    { monthDay: '05-12', name: 'Buddha Purnima', category: 'Gazetted' },
    { monthDay: '06-07', name: 'Bakrid (Eid al-Adha)', category: 'Gazetted' },
    { monthDay: '06-27', name: 'Rath Yatra', category: 'Hindu Festival' },
    { monthDay: '07-06', name: 'Muharram', category: 'Gazetted' },
    { monthDay: '07-10', name: 'Guru Purnima', category: 'Hindu Festival' },
    { monthDay: '08-09', name: 'Raksha Bandhan', category: 'Hindu Festival' },
    { monthDay: '08-16', name: 'Janmashtami (Krishna Janmashtami)', category: 'Hindu Festival' },
    { monthDay: '08-27', name: 'Ganesh Chaturthi', category: 'Hindu Festival' },
    { monthDay: '09-05', name: 'Milad-un-Nabi (Eid-e-Milad)', category: 'Gazetted' },
    { monthDay: '09-06', name: 'Anant Chaturdashi (Ganesh Visarjan)', category: 'Hindu Festival' },
    { monthDay: '09-22', name: 'Sharad Navratri Begins', category: 'Hindu Festival' },
    { monthDay: '09-30', name: 'Maha Ashtami / Durga Ashtami', category: 'Hindu Festival' },
    { monthDay: '10-01', name: 'Maha Navami / Dussehra (Vijayadashami)', category: 'Hindu Festival' },
    { monthDay: '10-10', name: 'Karwa Chauth', category: 'Hindu Festival' },
    { monthDay: '10-18', name: 'Dhanteras', category: 'Hindu Festival' },
    { monthDay: '10-19', name: 'Kali Chaudas / Narak Chaturdashi', category: 'Hindu Festival' },
    { monthDay: '10-20', name: 'Diwali (Lakshmi Pujan)', category: 'Hindu Festival' },
    { monthDay: '10-21', name: 'Govardhan Puja / Gujarati Bestu Varas (New Year)', category: 'Hindu Festival' },
    { monthDay: '10-22', name: 'Bhai Dooj', category: 'Hindu Festival' },
    { monthDay: '10-27', name: 'Chhath Puja', category: 'Hindu Festival' },
    { monthDay: '11-05', name: 'Guru Nanak Jayanti', category: 'Gazetted' },
  ],
  2026: [
    { monthDay: '01-23', name: 'Vasant Panchami', category: 'Hindu Festival' },
    { monthDay: '02-15', name: 'Maha Shivratri', category: 'Hindu Festival' },
    { monthDay: '03-03', name: 'Holika Dahan', category: 'Hindu Festival' },
    { monthDay: '03-04', name: 'Holi / Dhuleti', category: 'Hindu Festival' },
    { monthDay: '03-19', name: 'Chaitra Navratri / Gudi Padwa / Ugadi', category: 'Hindu Festival' },
    { monthDay: '03-20', name: 'Eid-ul-Fitr (Ramzan Id)', category: 'Gazetted' },
    { monthDay: '03-27', name: 'Ram Navami', category: 'Hindu Festival' },
    { monthDay: '03-31', name: 'Mahavir Jayanti', category: 'Gazetted' },
    { monthDay: '04-03', name: 'Good Friday', category: 'Gazetted' },
    { monthDay: '04-05', name: 'Easter Sunday', category: 'Gazetted' },
    { monthDay: '05-02', name: 'Buddha Purnima', category: 'Gazetted' },
    { monthDay: '05-28', name: 'Bakrid (Eid al-Adha)', category: 'Gazetted' },
    { monthDay: '06-16', name: 'Rath Yatra', category: 'Hindu Festival' },
    { monthDay: '06-26', name: 'Muharram', category: 'Gazetted' },
    { monthDay: '07-28', name: 'Guru Purnima', category: 'Hindu Festival' },
    { monthDay: '08-27', name: 'Raksha Bandhan', category: 'Hindu Festival' },
    { monthDay: '09-03', name: 'Janmashtami (Krishna Janmashtami)', category: 'Hindu Festival' },
    { monthDay: '09-04', name: 'Milad-un-Nabi (Eid-e-Milad)', category: 'Gazetted' },
    { monthDay: '09-14', name: 'Ganesh Chaturthi (Vinayaka Chaturthi)', category: 'Hindu Festival' },
    { monthDay: '09-24', name: 'Anant Chaturdashi (Ganesh Visarjan)', category: 'Hindu Festival' },
    { monthDay: '10-11', name: 'Sharad Navratri Begins', category: 'Hindu Festival' },
    { monthDay: '10-19', name: 'Maha Ashtami / Durga Ashtami', category: 'Hindu Festival' },
    { monthDay: '10-20', name: 'Maha Navami / Dussehra (Vijayadashami)', category: 'Hindu Festival' },
    { monthDay: '10-29', name: 'Karwa Chauth', category: 'Hindu Festival' },
    { monthDay: '11-06', name: 'Dhanteras', category: 'Hindu Festival' },
    { monthDay: '11-07', name: 'Kali Chaudas / Narak Chaturdashi', category: 'Hindu Festival' },
    { monthDay: '11-08', name: 'Diwali (Lakshmi Pujan)', category: 'Hindu Festival' },
    { monthDay: '11-09', name: 'Govardhan Puja / Gujarati Bestu Varas (New Year)', category: 'Hindu Festival' },
    { monthDay: '11-10', name: 'Bhai Dooj', category: 'Hindu Festival' },
    { monthDay: '11-15', name: 'Chhath Puja', category: 'Hindu Festival' },
    { monthDay: '11-24', name: 'Guru Nanak Jayanti', category: 'Gazetted' },
  ],
  2027: [
    { monthDay: '02-11', name: 'Vasant Panchami', category: 'Hindu Festival' },
    { monthDay: '03-06', name: 'Maha Shivratri', category: 'Hindu Festival' },
    { monthDay: '03-22', name: 'Holika Dahan', category: 'Hindu Festival' },
    { monthDay: '03-23', name: 'Holi / Dhuleti', category: 'Hindu Festival' },
    { monthDay: '04-07', name: 'Gudi Padwa / Chaitra Navratri', category: 'Hindu Festival' },
    { monthDay: '04-15', name: 'Ram Navami', category: 'Hindu Festival' },
    { monthDay: '04-20', name: 'Mahavir Jayanti', category: 'Gazetted' },
    { monthDay: '04-23', name: 'Good Friday', category: 'Gazetted' },
    { monthDay: '05-20', name: 'Buddha Purnima', category: 'Gazetted' },
    { monthDay: '08-17', name: 'Raksha Bandhan', category: 'Hindu Festival' },
    { monthDay: '08-25', name: 'Janmashtami (Krishna Janmashtami)', category: 'Hindu Festival' },
    { monthDay: '09-04', name: 'Ganesh Chaturthi', category: 'Hindu Festival' },
    { monthDay: '09-14', name: 'Anant Chaturdashi', category: 'Hindu Festival' },
    { monthDay: '09-30', name: 'Navratri Begins', category: 'Hindu Festival' },
    { monthDay: '10-09', name: 'Maha Ashtami', category: 'Hindu Festival' },
    { monthDay: '10-10', name: 'Dussehra (Vijayadashami)', category: 'Hindu Festival' },
    { monthDay: '10-18', name: 'Karwa Chauth', category: 'Hindu Festival' },
    { monthDay: '10-27', name: 'Dhanteras', category: 'Hindu Festival' },
    { monthDay: '10-28', name: 'Diwali (Lakshmi Pujan)', category: 'Hindu Festival' },
    { monthDay: '10-29', name: 'Govardhan Puja / Bestu Varas', category: 'Hindu Festival' },
    { monthDay: '10-30', name: 'Bhai Dooj', category: 'Hindu Festival' },
    { monthDay: '11-04', name: 'Chhath Puja', category: 'Hindu Festival' },
    { monthDay: '11-13', name: 'Guru Nanak Jayanti', category: 'Gazetted' },
  ],
};

/**
 * Get all Sundays in a given month (YYYY-MM)
 */
export const getMonthSundays = (yearMonth: string): HolidaySuggestion[] => {
  const [yearStr, monthStr] = yearMonth.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  if (isNaN(year) || isNaN(month)) return [];

  const totalDays = new Date(year, month, 0).getDate();
  const sundays: HolidaySuggestion[] = [];

  for (let day = 1; day <= totalDays; day++) {
    const d = new Date(year, month - 1, day);
    if (d.getDay() === 0) {
      const dayStr = String(day).padStart(2, '0');
      sundays.push({
        date: `${yearMonth}-${dayStr}`,
        name: `Sunday (${day} ${d.toLocaleDateString('en-US', { month: 'short' })})`,
        category: 'Weekend',
      });
    }
  }

  return sundays;
};

/**
 * Get all Indian & Hindu festival suggestions for a specific month (YYYY-MM)
 */
export const getIndianHolidaySuggestions = (yearMonth: string): HolidaySuggestion[] => {
  const [yearStr, monthStr] = yearMonth.split('-');
  const year = parseInt(yearStr, 10);
  if (isNaN(year) || !monthStr) return [];

  const targetMM = monthStr.padStart(2, '0');
  const resultMap = new Map<string, HolidaySuggestion>();

  // 1. Add fixed annual national holidays for this month
  for (const item of FIXED_ANNUAL_HOLIDAYS) {
    if (item.monthDay.startsWith(targetMM)) {
      const fullDate = `${yearStr}-${item.monthDay}`;
      resultMap.set(fullDate, {
        date: fullDate,
        name: item.name,
        category: item.category,
        isCommon: true,
      });
    }
  }

  // 2. Add year-specific festivals
  const yearFestivals = FESTIVALS_BY_YEAR[year] || [];
  for (const item of yearFestivals) {
    if (item.monthDay.startsWith(targetMM)) {
      const fullDate = `${yearStr}-${item.monthDay}`;
      if (!resultMap.has(fullDate)) {
        resultMap.set(fullDate, {
          date: fullDate,
          name: item.name,
          category: item.category,
          isCommon: true,
        });
      } else {
        // Append or refine festival name if same date
        const existing = resultMap.get(fullDate)!;
        existing.name = `${existing.name} / ${item.name}`;
      }
    }
  }

  // Sort by date ascending
  return Array.from(resultMap.values()).sort((a, b) => a.date.localeCompare(b.date));
};
